import { SchemaDefinition, UnknownSchema } from '@rehaje/core'

import {
  SchemaElementExcludesError,
  SchemaElementIncludesError
} from './errors'

import type { ElementSpecific } from './TypedNode'
import type { SchemaMetadataSpecific, SpecificTypes } from '@rehaje/core'

export interface SchemaElementSpecific extends SchemaMetadataSpecific {
  /**
   * 默认片段
   */
  default?: ElementSpecific
  /**
   * 不包含具体的 Element.Xxx
   */
  excludes?: (`Element.${string}` & SpecificTypes)[]
  /**
   * 包含具体的 Element.Xxx
   */
  includes?: (`Element.${string}` & SpecificTypes)[]
  type: 'Schema.Element'

  value?: any
}

export class SchemaElementDefinition extends SchemaDefinition {
  override defaultsMerge(
    value: any,
    schema: SchemaElementSpecific,
    initialize: boolean
  ) {
    if (initialize) {
      if (value === undefined) return schema.default
      return value
    }
    if (schema.required && value === undefined) {
      return schema.default
    }
    return value
  }

  override getIn(schema: SchemaElementSpecific, path: PropertyKey[]) {
    if (path.length === 0) return schema
    return UnknownSchema
  }

  override infer(
    target: any,
    schema: SchemaElementSpecific
  ): SchemaElementSpecific {
    if (target?.type === 'Schema.Element') return target
    return {
      ...this.inferOnlySchema(schema),
      value: target
    }
  }

  override inferOnlySchema(schema: SchemaElementSpecific) {
    return schema
  }

  override inferTypeScript(schema: SchemaElementSpecific): string {
    if (schema.value?.$$typeof || schema.value?.__v_skip) return 'JSX.Element'
    return 'Element'
  }

  override prune(target: any): any {
    return target
  }

  override transform(target: any) {
    return target
  }

  override validate(target: any, schema: SchemaElementSpecific) {
    if (
      this.validateMetadata(target, schema, (target) => {
        return (
          !!target?.$$typeof ||
          !!target?.__v_skip ||
          this.isRelationSpecific(target)
        )
      })
    ) {
      return
    }
    if (schema.includes && !schema.includes.includes(target.type)) {
      this.raise(new SchemaElementIncludesError(schema.name, target.type))
    }
    if (schema.excludes && schema.excludes.includes(target.type)) {
      this.raise(new SchemaElementExcludesError(schema.name, target.type))
    }
  }
}

declare global {
  export interface RehajeSpecifics {
    'Schema.Element': SchemaElementSpecific
  }
  export interface RehajeTypedDefinitions {
    'Schema.Element': SchemaElementDefinition
  }
}
